import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";

// Trip files live on ClientLinq. Always fetch them through hub-service /get-pdf, which requires a
// login and validates the path; never link to the ClientLinq URL directly (it has no auth of its own).
const fetchClientlinqFile = async (fileName: string) => {
  const res = await axiosInstance.post(
    `/agents/get-pdf`,
    { path: encodeURIComponent(fileName) },
    { responseType: "arraybuffer" },
  );
  return new Blob([res.data], { type: "application/pdf" });
};

const getDisplayName = (fileName: string) => fileName.split("/").slice(-1)[0] || "file";

// Keep the blob alive long enough for the new tab to load it.
const BLOB_URL_LIFETIME_MS = 60_000;

const downloadBlob = (blob: Blob, name: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_LIFETIME_MS);
};

/** Previews PDFs in a new tab; other file types are downloaded since the browser can't preview them. */
export const openClientlinqFile = async (fileName?: string) => {
  if (!fileName) return;

  const isPdf = fileName.toLowerCase().endsWith(".pdf");
  // Open the tab before the request so the browser treats it as part of the click, not a popup.
  const previewTab = isPdf ? window.open("", "_blank") : null;

  try {
    const blob = await fetchClientlinqFile(fileName);

    if (!previewTab) {
      downloadBlob(blob, getDisplayName(fileName));
      return;
    }

    const blobUrl = URL.createObjectURL(blob);
    previewTab.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_LIFETIME_MS);
  } catch {
    previewTab?.close();
    toast.error("Unable to open this file");
  }
};

export const downloadClientlinqFile = async (fileName?: string) => {
  if (!fileName) return;

  try {
    downloadBlob(await fetchClientlinqFile(fileName), getDisplayName(fileName));
  } catch {
    toast.error("Unable to download this file");
  }
};
